import React, { useEffect, useState, useRef, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import { SiGithub } from "react-icons/si";
import {
  FiMail,
  FiArrowRight,
  FiArrowUp,
  FiCode,
  FiShield,
  FiBox,
} from "react-icons/fi";
import TechScroll from "../components/TechScroll";
import ExperienceTimeline from "../components/ExperienceTimeline";
import "../styles/Home.css";
import profilePic from "../assets/dp.jpg";
import SoftwareIcon from "../assets/icons/skills/software.svg";
import CyberIcon from "../assets/icons/skills/cyber.svg";
import DevopsIcon from "../assets/icons/skills/devops.svg";

/* ── Scroll dot sections ── */
const NAV_SECTIONS = [
  { id: "hero",       label: "Home"       },
  { id: "about",      label: "About Me"   },
  { id: "skills",     label: "Skills"     },
  { id: "experience", label: "Experience" },
];

const LABEL_MAP = {
  hero:       "Home",
  about:      "About Me",
  skills:     "Skills",
  experience: "Experience",
};

/* ── Skill cards data ── */
const SKILL_CARDS = [
  {
    id:    "software",
    label: "Software",
    icon:  SoftwareIcon,
    desc:  "Next.js, React, TypeScript, .NET (C#), Node.js, REST APIs.",
    cat:   "software",
  },
  {
    id:    "cyber",
    label: "Cyber Security",
    icon:  CyberIcon,
    desc:  "SIEM Engineering, Pentesting, Threat Detection, OWASP, OSINT.",
    cat:   "cyber",
  },
  {
    id:    "devops",
    label: "DevOps",
    icon:  DevopsIcon,
    desc:  "Kubernetes, Docker, CI/CD, AWS, Terraform, automation.",
    cat:   "devops",
  },
];

/* ═══════════════════════════════════════════════════════════════
   useCountUp
   Animates a number from 0 → target over `duration` ms.
   Fires once when the element scrolls into view (60% threshold).
   Non-numeric values (MSc, IE) are set instantly with no loop.
═══════════════════════════════════════════════════════════════ */
function useCountUp(target, duration = 2800) {
  const hasRun      = useRef(false);
  const observerRef = useRef(null);

  const refCallback = useCallback(
    (node) => {
      if (!node) return;

      observerRef.current = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting && !hasRun.current) {
            hasRun.current = true;
            observerRef.current.disconnect();

            /* Non-numeric — set text directly, skip animation */
            if (isNaN(parseFloat(String(target)))) {
              node.textContent = target;
              return;
            }

            const end    = parseFloat(String(target));
            const suffix = String(target).replace(/[0-9.]/g, ""); // e.g. "+"
            const start  = performance.now();

            const tick = (now) => {
              const elapsed  = now - start;
              const progress = Math.min(elapsed / duration, 1);
              const eased    = 1 - Math.pow(1 - progress, 3); // ease-out cubic
              node.textContent = Math.floor(eased * end) + suffix;
              if (progress < 1) requestAnimationFrame(tick);
            };

            requestAnimationFrame(tick);
          }
        },
        { threshold: 0.6 }
      );

      observerRef.current.observe(node);
    },
    [target, duration]
  );

  return refCallback;
}

/* ── Single animated stat card ── */
function StatCard({ number, label, delay = 0 }) {
  const countRef = useCountUp(number);

  return (
    <div
      className="stat-card stat-card--anim"
      style={{ "--anim-delay": `${delay}ms` }}
    >
      <span className="stat-number" ref={countRef}>
        {number}
      </span>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/* ── Animated stat grid ── */
function AnimatedStatGrid() {
  return (
    <div className="stat-grid">
      <StatCard number="9+"  label="Projects"        delay={0}   />
      <StatCard number="3"   label="Specialisations" delay={100} />
      <StatCard number="MSc" label="Cyber — NCI"     delay={200} />
      <StatCard number="IE"  label="Dublin"           delay={300} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   HOME
═══════════════════════════════════════════════════════════════ */
function Home() {
  const [activeSection,  setActiveSection]  = useState("hero");
  const [scrollProgress, setScrollProgress] = useState(0);
  const [sectionLabel,   setSectionLabel]   = useState("");
  const [showLabel,      setShowLabel]      = useState(false);
  const [activeRole,     setActiveRole]     = useState("all");

  /* Structured data */
  useEffect(() => {
    const script = document.createElement("script");
    script.type  = "application/ld+json";
    script.text  = JSON.stringify({
      "@context": "https://schema.org",
      "@type":    "Person",
      name:       "Biko Sulu",
      jobTitle:   "Full-Stack Software Engineer & Cybersecurity Analyst",
      url:        "https://bikosulu.com",
      sameAs: [
        "https://www.linkedin.com/in/tivz/",
        "https://github.com/thumbiko",
      ],
    });
    document.head.appendChild(script);
    return () => document.head.removeChild(script);
  }, []);

  /* Intersection observer for dot nav */
  useEffect(() => {
    const sections = document.querySelectorAll("section[id]");
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const id = entry.target.id;
            setActiveSection(id);
            setSectionLabel(LABEL_MAP[id] || id);
            setShowLabel(true);
            setTimeout(() => setShowLabel(false), 1200);
          }
        });
      },
      { threshold: 0.45 }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  /* Scroll progress bar */
  useEffect(() => {
    const handle = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handle);
    return () => window.removeEventListener("scroll", handle);
  }, []);

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  const scrollToTop = () =>
    document.getElementById("hero")?.scrollIntoView({ behavior: "smooth" });

  const isVisible = (roles) =>
    activeRole === "all" || roles.includes(activeRole);

  const emailUrl    = "mailto:thumbikonyasulu@ymail.com";
  const linkedinUrl = "https://www.linkedin.com/in/tivz/";
  const githubUrl   = "https://github.com/thumbiko";

  return (
    <div className="home">
      <Helmet>
        <title>Biko Sulu | Cyber Security Analyst · Full-Stack Engineer</title>
        <meta
          name="description"
          content="Biko Sulu — Full-Stack Software Engineer & Cybersecurity Analyst specialising in MERN Stack, AI applications, SIEM engineering, and DevSecOps. Based in Dublin, Ireland."
        />
        <meta property="og:title"       content="Biko Sulu | Full-Stack Engineer & Cybersecurity Analyst" />
        <meta property="og:description" content="Building secure, scalable systems across engineering, cloud, and cybersecurity." />
        <meta property="og:url"         content="https://bikosulu.com/" />
      </Helmet>

      {/* Scroll progress bar */}
      <div className="scroll-progress" style={{ width: `${scrollProgress}%` }} />

      {/* Section popup */}
      {showLabel && <div className="section-popup">{sectionLabel}</div>}

      {/* Side dot nav */}
      <nav className="scroll-nav" aria-label="Page sections">
        {NAV_SECTIONS.map((item) => (
          <button
            key={item.id}
            className={`nav-dot ${activeSection === item.id ? "active" : ""}`}
            onClick={() => scrollTo(item.id)}
            data-tooltip={item.label}
            aria-label={`Go to ${item.label}`}
          />
        ))}
      </nav>

      {/* ────────────────────────────────────────────────
          SECTION 1 — HERO
      ──────────────────────────────────────────────── */}
      <section className="hero-section" id="hero">
        <div className="hero-inner">

          <div className="profile-wrapper">
            <div className="profile-ring">
              <img
                src={profilePic}
                alt="Biko Sulu — Cybersecurity Analyst, Dublin"
                className="profile-pic"
              />
            </div>
          </div>

          <div className="hero-text">
            
              <span className="status-dot" />
              
            

            <h1 className="hero-name">
              Thumbiko<br /><span>Nyasulu</span>
            </h1>

            <p className="hero-subtitle">
              Cyber Security Analyst
              <span className="sep">·</span>
              Full-Stack Engineer
              <span className="sep">·</span>
              DevSecOps
            </p>

            <p className="hero-desc">
              I combine software engineering, cybersecurity, and cloud technologies
              to build secure, scalable systems. Based in Dublin, Ireland.
            </p>

            <div className="hero-ctas">
              <a href={emailUrl} className="cta-primary">
                Get in touch <FiArrowRight size={14} />
              </a>
              <Link to="/projects" className="cta-outline">
                View projects
              </Link>
            </div>

            <div className="social-row">
              <a href={linkedinUrl} target="_blank" rel="noopener noreferrer" className="social-link" aria-label="LinkedIn">
                <LinkedInIcon style={{ fontSize: 18 }} />
              </a>
              <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="social-link" aria-label="GitHub">
                <SiGithub size={18} />
              </a>
              <a href={emailUrl} className="social-link" aria-label="Email">
                <FiMail size={18} />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────
          SECTION 2 — ABOUT ME
      ──────────────────────────────────────────────── */}
      <section className="about-section" id="about">
        <div className="section-container">
          <div className="section-label-row">
            <span className="section-label">About Me</span>
          </div>
          <h2 className="section-title">
            The person<br />behind the <span>keyboard</span>
          </h2>

          <div className="about-grid">
            <div className="about-bio">
              <p>
                I'm a <strong>Cybersecurity Analyst</strong> and{" "}
                <strong>Full-Stack Developer</strong> with hands-on experience across
                SOC operations, SIEM engineering, cloud security, DevSecOps,
                AI-powered applications, and secure software development.
              </p>
              <p>
                Currently completing an <strong>MSc in Cyber Security</strong> at the
                National College of Ireland, with a focused track in threat detection,
                cloud infrastructure, offensive security, and scalable application
                engineering.
              </p>
              <p>
                Based in <strong>Dublin, Ireland</strong>. Available for freelance and
                contract opportunities.
              </p>

              <div className="about-actions">
                <a href={emailUrl} className="cta-primary sm">
                  Get in touch <FiArrowRight size={13} />
                </a>
                <a href={githubUrl} target="_blank" rel="noopener noreferrer" className="cta-outline sm">
                  GitHub
                </a>
              </div>
            </div>

            {/* ── Animated stat grid ── */}
            <AnimatedStatGrid />
          </div>
        </div>
      </section>

      {/* ────────────────────────────────────────────────
          SECTION 3 — SKILLS / TOOLSET
      ──────────────────────────────────────────────── */}
      <section className="skills-section" id="skills">
        <div className="section-container">
          <div className="section-label-row">
            <span className="section-label">Skills</span>
          </div>
          <h2 className="section-title">
            Toolset &amp; <span>Expertise</span>
          </h2>

          <div className="filter-row">
            {["all", "software", "cyber", "devops"].map((role) => (
              <button
                key={role}
                className={`filter-pill ${activeRole === role ? "active" : ""}`}
                onClick={() => setActiveRole(role)}
              >
                {role.toUpperCase()}
              </button>
            ))}
          </div>

          <div className="skill-cards-grid">
            {SKILL_CARDS.map(({ id, label, icon, desc, cat }) => (
  <div
    key={id}
    className={`skill-card-item ${!isVisible([cat]) ? "dim" : ""}`}
  >
    <div className="skill-card-icon">
      <img src={icon} alt={label} width={20} height={20} />
    </div>
    <h3>{label}</h3>
    <p>{desc}</p>
  </div>
))}
          </div>

          <TechScroll activeRole={activeRole} />
        </div>
      </section>

      {/* ────────────────────────────────────────────────
          SECTION 4 — EXPERIENCE TIMELINE
      ──────────────────────────────────────────────── */}
      <section className="experience-section" id="experience">
        <div className="section-container wide">
          <div className="section-label-row">
            <span className="section-label">Experience</span>
          </div>
          <h2 className="section-title">
            Professional <span>Roadmap</span>
          </h2>

          <ExperienceTimeline />

          <div className="exp-cta-row">
            <button className="cta-outline" onClick={scrollToTop}>
              <FiArrowUp size={13} /> Back to top
            </button>
            <Link to="/experience" className="cta-outline">
              Full timeline <FiArrowRight size={13} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Home;